export type PortalTeamBadgeTone = 'purple' | 'blue' | 'green' | 'orange' | 'red' | 'sky' | 'amber';

export type PortalTeamMember = {
  id: string;
  name: string;
  role: string;
  badgeLabel: string;
  badgeTone: PortalTeamBadgeTone;
  bio: string;
  email: string;
  phone?: string;
  linkedInUrl?: string;
};

export type PortalTeamOtherMember = {
  id: string;
  name: string;
  role: string;
  email: string;
};

export type PortalTeamChannel = {
  id: string;
  label: string;
  value: string;
  kind: 'email' | 'chat' | 'meetings' | 'docs';
  copyable?: boolean;
};

export type PortalTeamRaciItem = {
  id: string;
  question: string;
  answer: string;
  detail: string;
  tone: 'green' | 'blue' | 'amber' | 'purple';
};

export type PortalTeamCheckpoint = {
  id: string;
  label: string;
  done: boolean;
};

export type PortalTeamData = {
  projectId: string;
  barukMembers: PortalTeamMember[];
  clientMembers: PortalTeamMember[];
  otherMembers: PortalTeamOtherMember[];
  channels: PortalTeamChannel[];
  raci: PortalTeamRaciItem[];
  checkpoints: PortalTeamCheckpoint[];
};

export const portalTeamByProjectId: Record<string, PortalTeamData> = {
  crm: {
    projectId: 'crm',
    barukMembers: [
      {
        id: 'baruk-mariana',
        name: 'Mariana Silva',
        role: 'Gerente do projeto',
        badgeLabel: 'Líder do Projeto',
        badgeTone: 'purple',
        bio: 'Coordena o planejamento, as entregas e a comunicação com o cliente.',
        email: 'mariana.silva@baruk.com.br',
        phone: '+55 11 99999-1001',
      },
      {
        id: 'baruk-rafael',
        name: 'Rafael Oliveira',
        role: 'Desenvolvedor',
        badgeLabel: 'Desenvolvimento',
        badgeTone: 'blue',
        bio: 'Responsável pelas integrações e pelos módulos comerciais do CRM.',
        email: 'rafael.oliveira@baruk.com.br',
        phone: '+55 11 99999-1002',
      },
      {
        id: 'baruk-ana',
        name: 'Ana Souza',
        role: 'Analista de negócios',
        badgeLabel: 'Negócio',
        badgeTone: 'green',
        bio: 'Traduz os processos comerciais em requisitos e valida as entregas.',
        email: 'ana.souza@baruk.com.br',
        phone: '+55 11 99999-1003',
      },
      {
        id: 'baruk-bruno',
        name: 'Bruno Costa',
        role: 'Especialista técnico',
        badgeLabel: 'Infraestrutura',
        badgeTone: 'orange',
        bio: 'Cuida de ambientes, acessos e estabilidade das integrações.',
        email: 'bruno.costa@baruk.com.br',
        phone: '+55 11 99999-1004',
      },
    ],
    clientMembers: [
      {
        id: 'client-joao',
        name: 'João Santos',
        role: 'Patrocinador',
        badgeLabel: 'Decisor',
        badgeTone: 'red',
        bio: 'Aprova mudanças de escopo, prazo e liberação das entregas críticas.',
        email: 'joao.santos@empresaexemplo.com.br',
        phone: '+55 11 98888-2001',
        linkedInUrl: 'https://www.linkedin.com/',
      },
      {
        id: 'client-fernanda',
        name: 'Fernanda Lima',
        role: 'Líder de validação',
        badgeLabel: 'Validador',
        badgeTone: 'sky',
        bio: 'Coordena os testes de homologação e o aceite das funcionalidades.',
        email: 'fernanda.lima@empresaexemplo.com.br',
        phone: '+55 11 98888-2002',
        linkedInUrl: 'https://www.linkedin.com/',
      },
      {
        id: 'client-ricardo',
        name: 'Ricardo Alves',
        role: 'Usuário-chave',
        badgeLabel: 'Operação',
        badgeTone: 'amber',
        bio: 'Representa o time comercial no dia a dia e valida fluxos de uso.',
        email: 'ricardo.alves@empresaexemplo.com.br',
        phone: '+55 11 98888-2003',
        linkedInUrl: 'https://www.linkedin.com/',
      },
    ],
    otherMembers: [
      {
        id: 'other-ti',
        name: 'Equipe de TI',
        role: 'Infraestrutura do cliente',
        email: 'ti@empresaexemplo.com.br',
      },
      {
        id: 'other-juridico',
        name: 'Jurídico',
        role: 'Contratos e compliance',
        email: 'juridico@empresaexemplo.com.br',
      },
    ],
    channels: [
      {
        id: 'ch-email',
        label: 'E-mail do projeto',
        value: 'crm@baruk.com.br',
        kind: 'email',
        copyable: true,
      },
      {
        id: 'ch-chat',
        label: 'Chat do projeto',
        value: '#projeto-crm-exemplo',
        kind: 'chat',
        copyable: true,
      },
      {
        id: 'ch-meetings',
        label: 'Reuniões periódicas',
        value: 'Terças, 10h · Quinzenal',
        kind: 'meetings',
      },
      {
        id: 'ch-docs',
        label: 'Documentação',
        value: 'Pasta do projeto no portal',
        kind: 'docs',
      },
    ],
    raci: [
      {
        id: 'raci-decide',
        question: 'Quem decide?',
        answer: 'Patrocinador do cliente',
        detail: 'João Santos',
        tone: 'green',
      },
      {
        id: 'raci-execute',
        question: 'Quem executa?',
        answer: 'Equipe Baruk',
        detail: 'Mariana Silva e time',
        tone: 'blue',
      },
      {
        id: 'raci-consult',
        question: 'Quem é consultado?',
        answer: 'Usuários-chave',
        detail: 'Ricardo Alves e área comercial',
        tone: 'amber',
      },
      {
        id: 'raci-inform',
        question: 'Quem é informado?',
        answer: 'Stakeholders',
        detail: 'Todas as áreas envolvidas',
        tone: 'purple',
      },
    ],
    checkpoints: [
      { id: 'cp-channels', label: 'Canais de comunicação definidos', done: true },
      { id: 'cp-roles', label: 'Papéis e responsabilidades alinhados', done: true },
      { id: 'cp-meetings', label: 'Reuniões periódicas agendadas', done: false },
      { id: 'cp-raci', label: 'Matriz RACI validada', done: false },
    ],
  },
};
